<template>
  <div id="rg-playback-controller">
    <div class="container">
      <div class="ctl-panel-1">
        <div class="col-1">
          <playback-area
            :playbackRequestSettings="playbackRequestSettings"
          ></playback-area>
          <playback-time
            class="playback-now-time-box"
            :nowTime="nowTime"
          ></playback-time>
        </div>
        <div class="col-2">
          <playback-function-component
            @area-setting="onAreaSetting"
            @date-time-setting="onDateTimeSetting"
            @copy="onCopy"
          ></playback-function-component>
          <tooltip tooltip-text="履歴再生をリセット">
            <close-button @close="onClose"></close-button>
          </tooltip>
        </div>
      </div>
      <div class="ctl-panel-2">
        <div class="col-1">
          <play-button
            class="playback-play-btn-box"
            :playbackStatus="mPlaybackStatus"
            @play="play"
            @pause="pause"
            @rePlay="rePlay"
          ></play-button>
          <playback-speed-button
            class="playback-speed-btn-box"
            @updatePlaybackSpeed="setPlaybackSpeed($event)"
          ></playback-speed-button>
        </div>
        <div class="col-2">
          <playback-progressbar
            class="playback-progressbar-box"
            :dateRange="dateRange"
            :nowTime="nowTime"
            @onChange="onChange($event)"
            @onDragStart="onDragStart"
            @reachEndTime="reachEndTime($event)"
          ></playback-progressbar>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "vue-slider-component/theme/antd.css";
import { createNamespacedHelpers } from "vuex";

import PlayButton from "./PlayButton";
import PlaybackSpeedButton from "./PlaybackSpeedButton";
import PlaybackProgressbar from "./PlaybackProgressbar.vue";
import PlaybackTime from "./PlaybackTime.vue";
import PlaybackArea from "./PlaybackArea.vue";
import PlaybackFunctionComponent from "./PlaybackFunctionComponent";
import CloseButton from "./CloseButton";
import Tooltip from "./Tooltip.vue";

import C from "../../Constants";

const {
  mapActions: mapActionsOfRGPlayback,
  mapGetters: mapGettersOfRGPlayback,
  mapMutations: mapMutationsOfRGPlayback,
} = createNamespacedHelpers("radarGridPlayback");

export default {
  name: "RgPlaybackController",
  props: {
    playbackRequestSettings: {
      type: Object, // playbackRequestSettings
      default() {
        return {};
      },
    },
  },
  components: {
    PlayButton,
    PlaybackSpeedButton,
    PlaybackProgressbar,
    PlaybackTime,
    PlaybackArea,
    PlaybackFunctionComponent,
    CloseButton,
    Tooltip,
  },
  data() {
    return {
      nowTime: 0,
      dateRange: {
        startTime: 0,
        endTime: 0,
      },
      mPlaybackStatus: null,
    };
  },
  watch: {
    playbackSettings(val) {
      this.nowTime = val.now;
      this.dateRange.startTime = val.startTime;
      this.dateRange.endTime = val.endTime;
    },
    playbackTimeNow(val) {
      this.nowTime = val;
    },
    playbackStatus(val) {
      this.mPlaybackStatus = val;
    },
  },
  computed: {
    ...mapGettersOfRGPlayback([
      "playbackSettings",
      "playbackStartTime",
      "playbackTimeNow",
      "playbackStatus",
    ]),
  },
  methods: {
    ...mapActionsOfRGPlayback(["activateDateInputDialog"]),
    ...mapMutationsOfRGPlayback([
      "SET_PLAYBACK_TIME_NOW",
      "SET_RG_PLAYBACK_STATUS",
      "SET_PLAYBACK_SECONDS_PER_FRAME",
      "SET_LAYER_FILTERS",
    ]),
    setPlaybackSpeed(e) {
      this.SET_PLAYBACK_SECONDS_PER_FRAME(e);
    },
    onChange(e) {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
      this.SET_PLAYBACK_TIME_NOW(e);
    },
    onDragStart() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
    },
    reachEndTime(e) {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.REPLAY);
      this.SET_PLAYBACK_TIME_NOW(e);
    },
    play() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PLAY);
    },
    pause() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
    },
    rePlay() {
      this.SET_PLAYBACK_TIME_NOW(this.playbackStartTime);
      setTimeout(() => {
        this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PLAY);
      }, 1000);
    },
    onAreaSetting() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
      if (!this.playbackRequestSettings) return;
      this.playbackRequestSettings.toAreaSetting(this.$router);
    },
    onDateTimeSetting() {
      this.activateDateInputDialog();
    },
    async onCopy() {
      try {
        await navigator.clipboard.writeText(window.location.href);
        this.$vs.notify({
          text: `現在のURLをコピーしました`,
          color: "success",
        });
      } catch (error) {
        this.$vs.notify({
          text: "URLのコピーに失敗しました",
          color: "danger",
        });
      }
    },
    onClose() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
      if (!this.playbackRequestSettings) return;
      this.playbackRequestSettings.toAreaSettingWithDefaultPosition(
        this.$router
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

#rg-playback-controller {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  z-index: 2;
  width: 60vw;
  max-width: 800px;
  max-height: 112px;

  .container {
    width: 100%;
    height: 100%;
    background-color: $aisea-white;
    padding: 14px 20px;

    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 10px;

    .ctl-panel-1 {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      align-self: stretch;

      .col-1 {
        display: flex;
        align-items: center;
        gap: 25px;
        height: 36px;
      }
      .col-2 {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        align-self: stretch;
        gap: 30px;
      }
    }
    .ctl-panel-2 {
      display: flex;
      align-items: center;
      gap: 18px;
      .col-1 {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .col-2 {
        width: 100%;
      }
    }
  }
}
</style>
